import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogIn, KeyRound, AtSign, ShieldCheck } from "lucide-react";
import { useClientAuth, formatApiErrorDetail } from "./lib/auth";
import { useLang } from "@/lib/lang";

export default function ClientLogin() {
  const { user, loading, login } = useClientAuth();
  const nav = useNavigate();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/cliente" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, password); nav("/cliente"); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  const googleLogin = () => {
    const redirect = `${window.location.origin}/cliente/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const L = t?.clientArea?.login || {};

  return (
    <div className="min-h-screen theme-dark grain flex items-center justify-center px-6 relative overflow-hidden">
      {/* decorative gradient orbs */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent-orange/20 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-accent-yellow/15 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="relative w-full max-w-md bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-10"
        data-testid="client-login-card"
      >
        <Link to="/" className="font-display text-3xl tracking-tighter block mb-1">
          places<span className="text-accent-orange">.</span>
        </Link>
        <div className="text-xs uppercase tracking-widest text-accent-orange font-bold mb-6">
          {L.eyebrow || "Área do Cliente"}
        </div>

        <h1 className="font-display text-3xl md:text-4xl leading-tight mb-2">
          {L.title || "Bem-vindo de volta."}
        </h1>
        <p className="text-sm text-neutral-400 mb-8">
          {L.subtitle || "Acesse seu dashboard exclusivo com dados, documentos e projetos."}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
              <AtSign className="w-3 h-3" /> Email
            </label>
            <input
              value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
              data-testid="client-login-email"
              placeholder="seu@email.com"
              className="mt-1.5 w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-sm focus:border-accent-orange focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-neutral-500 flex items-center gap-1.5">
              <KeyRound className="w-3 h-3" /> Senha
            </label>
            <input
              value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
              data-testid="client-login-password"
              placeholder="••••••••"
              className="mt-1.5 w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 text-sm focus:border-accent-orange focus:outline-none transition-colors"
            />
          </div>
          {err && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="client-login-error">{err}</div>}
          <button
            type="submit" disabled={busy}
            data-testid="client-login-submit"
            className="w-full flex items-center justify-center gap-2 bg-accent-orange hover:bg-accent-yellow hover:text-black text-white font-semibold py-3.5 rounded-full transition-all disabled:opacity-50 shadow-lg"
          >
            <LogIn className="w-4 h-4" /> {busy ? "Entrando…" : (L.submit || "Entrar")}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-neutral-500">ou</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button
          onClick={googleLogin}
          data-testid="client-login-google"
          className="w-full flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 hover:bg-white/5 text-sm py-3.5 rounded-full transition-colors"
        >
          <ShieldCheck className="w-4 h-4" /> Entrar com Google
        </button>

        <div className="mt-6 text-[11px] text-neutral-500 text-center leading-relaxed">
          Cadastro criado pelo seu atendimento. Se você não tem acesso, fale com{" "}
          <a href="mailto:contato@agenciaplaces.com.br" className="text-accent-orange hover:underline">contato@agenciaplaces.com.br</a>.
        </div>
      </motion.div>
    </div>
  );
}
